import React, { useMemo } from 'react';
import { styles } from '../../styles/globalStyles';
import {
  charLimitsFromLines,
  intentionLetterFieldStyle,
  linesFromCharCount,
  sumRubricScores,
  validateIntentionLetterText,
} from '../../utils/intentionLetter';

export function IntentionLetterCandidateField({
  course,
  value,
  onChange,
  disabled = false,
  error = '',
}) {
  const minLines = course?.letter_min_lines ?? 0;
  const maxLines = course?.letter_max_lines ?? 0;
  const { minCharacters, maxCharacters } = charLimitsFromLines(minLines, maxLines);
  const normalized = String(value || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n+/g, ' ');
  const charCount = normalized.length;
  const lineCount = linesFromCharCount(charCount);

  const handleChange = (e) => {
    let next = e.target.value.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n+/g, ' ');
    if (next.length > maxCharacters) {
      next = next.slice(0, maxCharacters);
    }
    onChange(next);
  };

  return (
    <div style={{ marginTop: '12px' }}>
      {course?.letter_instructions ? (
        <p style={{ fontSize: '13px', color: '#374151', marginBottom: '8px', whiteSpace: 'pre-wrap' }}>
          {course.letter_instructions}
        </p>
      ) : null}
      <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: '8px' }}>
        Mínimo {minLines} linha(s) ({minCharacters} caracteres) · Máximo {maxLines} linha(s) ({maxCharacters} caracteres)
      </p>
      <textarea
        value={value}
        onChange={handleChange}
        disabled={disabled}
        rows={Math.max(maxLines, 4)}
        style={{
          ...intentionLetterFieldStyle,
          minHeight: `${Math.max(maxLines, 4) * 18}px`,
          border: error ? '1px solid #DC2626' : '1px solid #D1D5DB',
          borderRadius: '6px',
          padding: '10px',
        }}
        placeholder="Digite sua carta de intenção..."
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6B7280', marginTop: '6px' }}>
        <span>{lineCount}/{maxLines} linhas</span>
        <span>{charCount}/{maxCharacters} caracteres</span>
      </div>
      {error ? <p style={{ color: '#DC2626', fontSize: '12px', marginTop: '6px' }}>{error}</p> : null}
    </div>
  );
}

export function IntentionLetterReadOnly({ text, course }) {
  const content = String(text || '');
  const maxLines = course?.letter_max_lines ?? 0;
  const { maxCharacters } = charLimitsFromLines(0, maxLines);
  return (
    <div style={{ marginBottom: '16px' }}>
      <h4 style={{ margin: '0 0 8px 0', fontSize: '14px' }}>Carta de intenção</h4>
      <div
        style={{
          ...intentionLetterFieldStyle,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          background: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: '6px',
          padding: '12px',
          maxHeight: '320px',
          overflowY: 'auto',
        }}
      >
        {content || '—'}
      </div>
      <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '6px' }}>
        {linesFromCharCount(content.length)}/{maxLines} linhas · {content.length}/{maxCharacters} caracteres
      </p>
    </div>
  );
}

export function IntentionLetterRubricForm({ course, scores, onScoreChange, disabled = false }) {
  const rubric = Array.isArray(course?.letter_rubric) ? course.letter_rubric : [];
  const total = useMemo(() => sumRubricScores(rubric, scores), [rubric, scores]);

  if (!rubric.length) {
    return <p style={{ color: '#DC2626', fontSize: '13px' }}>Barema não configurado no curso.</p>;
  }

  return (
    <div>
      <h4 style={{ margin: '0 0 12px 0', fontSize: '14px' }}>Barema — critérios de avaliação</h4>
      {rubric.map((row) => (
        <div key={row.id} style={{ ...styles.formRow, alignItems: 'center', marginBottom: '10px' }}>
          <div style={{ flex: 1, fontSize: '13px', paddingRight: '12px' }}>
            {row.label}
            <span style={{ color: '#6B7280' }}> (máx. {row.max_points} pts)</span>
          </div>
          <input
            type="number"
            min={0}
            max={row.max_points}
            step="0.01"
            disabled={disabled}
            value={scores[row.id] ?? ''}
            onChange={(e) => onScoreChange(row.id, e.target.value)}
            style={{ width: '100px', padding: '6px 8px', borderRadius: '4px', border: '1px solid #D1D5DB' }}
          />
        </div>
      ))}
      <p style={{ fontWeight: 600, fontSize: '14px', marginTop: '12px' }}>
        Nota final: {total.toFixed(2)}
      </p>
    </div>
  );
}

export function validateIntentionLetterForCourse(course, text) {
  return validateIntentionLetterText(
    text,
    course?.letter_min_lines ?? 0,
    course?.letter_max_lines ?? 0,
  );
}

export function validateRubricForm(course, scores) {
  const rubric = Array.isArray(course?.letter_rubric) ? course.letter_rubric : [];
  if (!rubric.length) {
    return { ok: false, message: 'Barema não configurado no curso' };
  }
  for (const row of rubric) {
    const raw = scores[row.id];
    if (raw === '' || raw === undefined || raw === null) {
      return { ok: false, message: `Informe a nota: ${row.label}` };
    }
    const val = parseFloat(raw);
    if (!Number.isFinite(val) || val < 0 || val > Number(row.max_points)) {
      return { ok: false, message: `Nota inválida em "${row.label}" (0 a ${row.max_points})` };
    }
  }
  return { ok: true, total: sumRubricScores(rubric, scores) };
}
